<template>
  <div class="ai-admin-page">
    <header class="ai-page-header">
      <div>
        <h1>模型选择</h1>
        <p>
          配置 AI 客服使用的千问模型、生成参数和转人工规则。API Key
          只从后端环境变量读取，页面不会保存或回显密钥。
        </p>
      </div>
      <div class="ai-status-line">
        <span class="ai-status-dot" :class="{ 'is-ready': form.apiKeyConfigured }" />{{
          form.apiKeyConfigured ? "服务端密钥已配置" : "服务端密钥未配置"
        }}
      </div>
    </header>

    <el-form
      ref="form"
      v-loading="loading"
      :model="form"
      :rules="rules"
      label-position="top"
      class="ai-surface model-form"
    >
      <section class="ai-section">
        <div class="ai-section__heading">
          <div>
            <h2>服务状态</h2>
            <p>关闭后新消息继续使用现有人工客服流程，已经保存的配置不会丢失。</p>
          </div>
          <el-switch v-model="form.enabled" active-text="启用 AI 客服" inactive-text="仅人工客服" />
        </div>
        <div class="ai-note">
          <i class="el-icon-lock" /><span
            >密钥读取顺序：服务器配置 <code>kefu.ai.api-key</code>，其次为环境变量
            <code>DASHSCOPE_API_KEY</code>。</span
          >
        </div>
      </section>

      <section class="ai-section">
        <div class="ai-section__heading">
          <div>
            <h2>千问模型</h2>
            <p>首期建议使用千问 Plus，兼顾客服回答质量、工具调用和响应速度。</p>
          </div>
        </div>
        <el-row :gutter="20">
          <el-col :xs="24" :md="12">
            <el-form-item label="模型供应商"
              ><el-input value="阿里云百炼 / 通义千问" disabled
            /></el-form-item>
          </el-col>
          <el-col :xs="24" :md="12">
            <el-form-item label="模型" prop="modelId">
              <el-select
                v-model="form.modelId"
                filterable
                allow-create
                default-first-option
                style="width: 100%"
                placeholder="选择或输入模型ID"
              >
                <el-option label="千问 Plus（推荐）" value="qwen-plus" />
                <el-option label="千问 3.7 Plus" value="qwen3.7-plus" />
                <el-option label="千问 3.6 Flash" value="qwen3.6-flash" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="OpenAI 兼容地址" prop="baseUrl">
              <el-input
                v-model.trim="form.baseUrl"
                placeholder="https://...aliyuncs.com/compatible-mode/v1"
              />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="8">
            <el-form-item label="生成温度" prop="temperature"
              ><el-input-number
                v-model="form.temperature"
                :min="0"
                :max="1"
                :step="0.1"
                :precision="2"
                controls-position="right"
                style="width: 100%"
            /></el-form-item>
          </el-col>
          <el-col :xs="24" :md="8">
            <el-form-item label="最大输出 Token" prop="maxTokens"
              ><el-input-number
                v-model="form.maxTokens"
                :min="100"
                :max="4000"
                :step="100"
                controls-position="right"
                style="width: 100%"
            /></el-form-item>
          </el-col>
          <el-col :xs="24" :md="8">
            <el-form-item label="超时时间（秒）" prop="requestTimeoutSeconds"
              ><el-input-number
                v-model="form.requestTimeoutSeconds"
                :min="5"
                :max="60"
                controls-position="right"
                style="width: 100%"
            /></el-form-item>
          </el-col>
        </el-row>
      </section>

      <section class="ai-section">
        <div class="ai-section__heading">
          <div>
            <h2>客服规则</h2>
            <p>系统提示词决定回答边界；关键词由后端最终判断，用户端只负责展示状态。</p>
          </div>
        </div>
        <el-form-item label="系统提示词" prop="systemPrompt">
          <el-input
            v-model="form.systemPrompt"
            type="textarea"
            :rows="7"
            maxlength="4000"
            show-word-limit
          />
        </el-form-item>
        <el-row :gutter="20">
          <el-col :xs="24" :md="12">
            <el-form-item label="转人工关键词">
              <el-select
                v-model="form.transferKeywords"
                multiple
                filterable
                allow-create
                default-first-option
                style="width: 100%"
                placeholder="输入后回车添加"
              >
                <el-option
                  v-for="item in form.transferKeywords"
                  :key="item"
                  :label="item"
                  :value="item"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="12">
            <el-form-item label="否定词（不触发人工）">
              <el-select
                v-model="form.negativeKeywords"
                multiple
                filterable
                allow-create
                default-first-option
                style="width: 100%"
                placeholder="输入后回车添加"
              >
                <el-option
                  v-for="item in form.negativeKeywords"
                  :key="item"
                  :label="item"
                  :value="item"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
      </section>

      <footer class="ai-form-actions">
        <span v-if="testResult" class="connection-result"
          ><i class="el-icon-success" />{{ testResult }}</span
        >
        <el-button :loading="testing" @click="testConnection">测试模型连接</el-button>
        <el-button type="primary" :loading="saving" @click="save">保存模型配置</el-button>
      </footer>
    </el-form>
  </div>
</template>

<script>
import { getKefuAiConfig, saveKefuAiConfig, testKefuAiConnection } from "@/api/kefu";

export default {
  name: "KefuModelSettings",
  data() {
    return {
      loading: false,
      saving: false,
      testing: false,
      testResult: "",
      form: {
        enabled: false,
        provider: "ALIYUN",
        modelId: "qwen-plus",
        baseUrl: "https://dashscope.aliyuncs.com/compatible-mode/v1",
        temperature: 0.3,
        maxTokens: 800,
        requestTimeoutSeconds: 20,
        systemPrompt: "",
        transferKeywords: [],
        negativeKeywords: [],
        apiKeyConfigured: false
      },
      rules: {
        modelId: [{ required: true, message: "请选择或输入模型ID", trigger: "change" }],
        baseUrl: [{ required: true, message: "请输入模型接口地址", trigger: "blur" }],
        systemPrompt: [{ required: true, message: "请输入系统提示词", trigger: "blur" }]
      }
    };
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const data = await getKefuAiConfig();
        this.form = { ...this.form, ...(data || {}) };
      } finally {
        this.loading = false;
      }
    },
    payload() {
      const { apiKeyConfigured, provider, id, ...payload } = this.form;
      return payload;
    },
    save() {
      this.$refs.form.validate(async valid => {
        if (!valid) return;
        this.saving = true;
        try {
          const data = await saveKefuAiConfig(this.payload());
          this.form = { ...this.form, ...(data || {}) };
          this.$message.success("模型配置已保存");
        } finally {
          this.saving = false;
        }
      });
    },
    async testConnection() {
      const valid = await new Promise(resolve =>
        this.$refs.form.validate(result => resolve(result))
      );
      if (!valid) return;
      this.testing = true;
      this.testResult = "";
      try {
        const data = await testKefuAiConnection(this.payload());
        this.testResult = `${data.message}，耗时 ${data.latencyMs}ms`;
        this.$message.success("模型连接正常");
      } finally {
        this.testing = false;
      }
    }
  }
};
</script>

<style lang="scss">
@import "./ai-management.scss";
.model-form {
  max-width: 1080px;
}
.model-form code {
  padding: 1px 5px;
  border-radius: 4px;
  background: rgba(121, 52, 246, 0.1);
  color: #4d2f8f;
  font-family: Consolas, monospace;
}
.connection-result {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-right: auto;
  color: #168559;
  font-size: 13px;
}
.model-form .el-select .el-tag {
  max-width: 220px;
}
</style>
